import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { Button, Card, Tag } from 'antd'
import { DownOutlined, UpOutlined, WarningOutlined } from '@ant-design/icons'

import { formatDate, formatDateTime } from './journeyTime'
import { buyerKey, channelKey, creditOf, durationOf, joined, reasonKey, stateKey, wordsFor } from './journeyWords'
import { activityOf, failedCheckouts, productsNotInOrder, quoteOpensOf } from './journeyFacts'
import { DEPOSIT_PAID, similarNewerOrder, similarOlderPayment } from './afterOrderFacts'
import { afterSummaryText } from './afterOrderWords'
import { HintLabel } from './HintLabel'
import { Muted, Panel, SummaryLabel, SummaryList, SummaryRow, SummaryValue, TagRow } from './journeyStyles'

const CREDIT_KEYS = {
  code: 'orderJourney.creditCode',
  link: 'orderJourney.creditLink',
  message: 'orderJourney.creditMessage',
  holdout: 'orderJourney.creditHoldout'
}

const creditRow = (t, journey, lang) => {
  const credit = creditOf(journey.attribution, journey.order?.started_at)
  if (!credit) return { value: null }
  const text = t(CREDIT_KEYS[credit.level], {
    flow: credit.flowName,
    date: formatDateTime(credit.exposedAt, lang)
  })
  return {
    value: joined([
      text,
      credit.afterOrder && t('orderJourney.creditAfterOrder'),
      credit.colleague && t('orderJourney.creditColleague')
    ]),
    muted: credit.level === 'holdout',
    strong: credit.level !== 'holdout'
  }
}

const cameRow = (t, journey, lang) => {
  const order = journey.order || {}
  return joined([
    order.buyer && wordsFor(journey, buyerKey(order.buyer), lang),
    order.first_event && wordsFor(journey, order.first_event, lang),
    order.from_checkout && t('orderJourney.fromCheckout')
  ])
}

const visitRow = (journey, lang) => {
  const visit = journey.visit_source
  if (!visit) return null
  return joined([
    visit.channel && wordsFor(journey, channelKey(visit.channel), lang),
    visit.utm_campaign,
    visit.at && formatDateTime(visit.at, lang)
  ])
}

const activityRow = (t, journey, lang) => {
  const activity = activityOf(journey.steps || [])
  if (!activity) return null
  const key = journey.window?.complete === false ? 'orderJourney.activityAtLeast' : 'orderJourney.activity'
  return t(key, { visits: activity.visits, days: activity.days, date: formatDate(activity.firstAt, lang) })
}

const firstVisitRow = (journey, lang) => {
  const touch = journey.first_touch
  if (!touch) return null
  const origin = touch.origin || {}
  return joined([
    touch.at && formatDate(touch.at, lang),
    touch.channel && wordsFor(journey, channelKey(touch.channel), lang),
    [origin.source, origin.medium, origin.campaign].filter(Boolean).join(' / ')
  ])
}

const summaryRows = (t, journey, lang) => {
  const quoteOpens = quoteOpensOf(journey)
  return [
    { key: 'credit', label: t('orderJourney.creditLabel'), hint: t('orderJourney.creditHint'), ...creditRow(t, journey, lang) },
    { key: 'came', label: t('orderJourney.cameLabel'), hint: t('orderJourney.cameHint'), value: cameRow(t, journey, lang) },
    { key: 'visit', label: t('orderJourney.visitLabel'), hint: t('orderJourney.visitHint'), value: visitRow(journey, lang) },
    { key: 'activity', label: t('orderJourney.activityLabel'), hint: t('orderJourney.activityHint'), value: activityRow(t, journey, lang) },
    quoteOpens && {
      key: 'quote',
      label: t('orderJourney.quoteLabel'),
      hint: t('orderJourney.quoteHint'),
      value: t('orderJourney.quoteOpens', { count: quoteOpens.count, date: formatDateTime(quoteOpens.last_at, lang) })
    },
    { key: 'after', label: t('orderJourney.afterLabel'), hint: t('orderJourney.afterHint'), value: afterSummaryText(t, journey, lang), strong: true },
    { key: 'firstVisit', label: t('orderJourney.firstVisitLabel'), hint: t('orderJourney.firstVisitHint'), value: firstVisitRow(journey, lang), muted: true }
  ].filter((row) => row && row.value)
}

const JourneySummary = ({ journey, orderIds, lang }) => {
  const { t } = useTranslation('inquiries')
  const [showProducts, setShowProducts] = useState(false)
  const failed = failedCheckouts(journey.steps || [])
  const otherProducts = productsNotInOrder(journey, orderIds)
  const similar = similarNewerOrder(journey.after_order, orderIds)
  const paidElsewhere = similarOlderPayment(journey.after_order, orderIds)

  return (
    <Card size='small' title={t('orderJourney.title')} extra={<Muted $small>{t('orderJourney.source')}</Muted>}>
      <SummaryList>
        {summaryRows(t, journey, lang).map((row) => (
          <SummaryRow key={row.key}>
            <SummaryLabel>
              <HintLabel label={row.label} hint={row.hint} />
            </SummaryLabel>
            <SummaryValue $muted={row.muted} $strong={row.strong}>{row.value}</SummaryValue>
          </SummaryRow>
        ))}
      </SummaryList>
      {(paidElsewhere || similar || failed.count > 0 || otherProducts.length > 0) && (
        <TagRow>
          {paidElsewhere && (
            <Tag color='orange' icon={<WarningOutlined />}>
              {t(paidElsewhere.step.name === DEPOSIT_PAID ? 'orderJourney.olderDepositTag' : 'orderJourney.olderPaidTag', {
                number: (paidElsewhere.step.details || [])[0] || '',
                shared: paidElsewhere.shared.shared,
                total: paidElsewhere.shared.total,
                duration: durationOf(t, journey.order?.started_at, paidElsewhere.step.first_at)
              })}
            </Tag>
          )}
          {similar && (
            <Tag color='orange' icon={<WarningOutlined />}>
              {t('orderJourney.similarOrderTag', {
                number: similar.order.order_number || `#${similar.order.order_id}`,
                shared: similar.shared.shared,
                total: similar.shared.total,
                state: wordsFor(journey, stateKey(similar.order.state), lang)
              })}
            </Tag>
          )}
          {failed.count > 0 && (
            <Tag color='warning' icon={<WarningOutlined />}>
              {[
                t('orderJourney.failedTag', { count: failed.count }),
                failed.reasons.map((code) => wordsFor(journey, reasonKey(code), lang)).join(', ')
              ]
                .filter(Boolean)
                .join(': ')}
            </Tag>
          )}
          {otherProducts.length > 0 && (
            <Button
              size='small'
              aria-expanded={showProducts}
              icon={showProducts ? <UpOutlined /> : <DownOutlined />}
              iconPosition='end'
              onClick={() => setShowProducts(!showProducts)}
            >
              {t('orderJourney.otherProductsTag', { count: otherProducts.length })}
            </Button>
          )}
        </TagRow>
      )}
      {showProducts && (
        <Panel style={{ marginTop: 8 }}>
          {otherProducts.map((product) => (
            <div key={product.id}>{product.name || `#${product.id}`}</div>
          ))}
        </Panel>
      )}
    </Card>
  )
}

JourneySummary.propTypes = {
  journey: PropTypes.object.isRequired,
  orderIds: PropTypes.instanceOf(Set).isRequired,
  lang: PropTypes.string.isRequired
}

export { JourneySummary }
